import { useQueries } from "@tanstack/react-query";
import { activitiesRepository } from "../repositories/activitiesRepository";
import { eventsRepository } from "../repositories/eventsRepository";
import { mediumRepository } from "../repositories/mediumRepository";
import { profileRepository } from "../repositories/profileRepository";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { mediumStatusQueryKey } from "./useMediumStatusQuery";

// Query keys - centralized for consistency
export const homeQueryKeys = {
  profile: ["profile"] as const,
  homeProfile: ["profile", "home"] as const,
  upcomingEvents: (terreiroId?: string) =>
    terreiroId
      ? (["terreiro", terreiroId, "events", "upcoming"] as const)
      : (["events", "upcoming", "no-terreiro"] as const),
  recentActivities: (terreiroId?: string) =>
    terreiroId
      ? (["terreiro", terreiroId, "activities", "recent"] as const)
      : (["personal", "activities", "recent"] as const),
  /** Prefix of {@link mediumStatusQueryKey}. Invalidation matches every medium. */
  mediumStatus: ["medium", "me", "status"] as const,
  mediumUserData: ["medium", "me", "data"] as const,
};

export const useHomeQueries = () => {
  const cachedTerreiro = useCachedTerreiro();
  const cachedMediumId = useCachedMedium()?.id;
  const terreiroId = cachedTerreiro?.id;

  const queries = useQueries({
    queries: [
      {
        queryKey: homeQueryKeys.homeProfile,
        queryFn: () => profileRepository.getProfile(),
        staleTime: 1000 * 60 * 15, // 15 minutes - profile changes infrequently
        gcTime: 60 * 60 * 1000, // Cache for 1 hour
      },
      {
        queryKey: homeQueryKeys.upcomingEvents(terreiroId),
        queryFn: () => eventsRepository.getUpcomingEvents(terreiroId),
        staleTime: 1000 * 60 * 2, // 2 minutes - events change moderately
        gcTime: 10 * 60 * 1000, // Cache for 10 minutes
        refetchInterval: 1000 * 60 * 5, // Auto-refetch every 5 minutes
      },
      {
        queryKey: homeQueryKeys.recentActivities(terreiroId),
        queryFn: () => activitiesRepository.getRecentActivities(terreiroId),
        staleTime: 1000 * 30, // 30 seconds - activities change frequently
        gcTime: 2 * 60 * 1000, // Cache for 2 minutes
        refetchInterval: 1000 * 60, // Auto-refetch every minute
      },
      {
        queryKey: mediumStatusQueryKey(cachedMediumId),
        queryFn: () => mediumRepository.getMyStatus(cachedMediumId),
        enabled: !!cachedMediumId,
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 60 * 60 * 1000, // 1 hour
      },
      {
        queryKey: homeQueryKeys.mediumUserData,
        queryFn: () => mediumRepository.getMyMediumData(),
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 60 * 60 * 1000, // 1 hour
      },
    ],
  });

  const [
    profileQuery,
    eventsQuery,
    activitiesQuery,
    mediumStatusQuery,
    mediumUserDataQuery,
  ] = queries;

  return {
    // Data
    user: profileQuery.data,
    upcomingEvents: eventsQuery.data || [],
    recentActivities: activitiesQuery.data || [],
    mediumStatus: mediumStatusQuery.data,
    mediumUserData: mediumUserDataQuery.data,

    // Loading states
    isProfileLoading: profileQuery.isLoading,
    isEventsLoading: eventsQuery.isLoading,
    isActivitiesLoading: activitiesQuery.isLoading,
    isMediumStatusLoading: mediumStatusQuery.isLoading,
    isMediumUserDataLoading: mediumUserDataQuery.isLoading,
    isAnyLoading: queries.some((q) => q.isLoading),

    // Error states
    profileError: profileQuery.error,
    eventsError: eventsQuery.error,
    activitiesError: activitiesQuery.error,
    mediumStatusError: mediumStatusQuery.error,
    mediumUserDataError: mediumUserDataQuery.error,
    hasAnyError: queries.some((q) => q.error),

    // Actions
    refetchAll: () => queries.forEach((q) => q.refetch()),
    refetchProfile: profileQuery.refetch,
    refetchEvents: eventsQuery.refetch,
    refetchActivities: activitiesQuery.refetch,
    refetchMediumStatus: mediumStatusQuery.refetch,
    refetchMediumUserData: mediumUserDataQuery.refetch,

    // Status
    isSuccess: queries.every((q) => q.isSuccess),
    isError: queries.some((q) => q.isError),
  };
};
